import * as Cesium from 'cesium'
import CesiumTools from './CesiumTools'
import CesiumConstellationsTools from './CesiumConstellationsTools'

export const AnalyticalOrbit = {
  data() {
    return {
      orbitalPlanesCollection: null,
      orbitTracesPC: null,
      secondPolylineCollection: null
    }
  },
  methods: {
    initTracesPC() {
      this.orbitTracesPC = new Cesium.PolylineCollection()
      this.viewer.scene.primitives.add(this.orbitTracesPC)
      this.orbitTracesPC.show = true
    },

    initOneTracesPC() {
      let tracePC = new Cesium.PolylineCollection()
      this.viewer.scene.primitives.add(tracePC)
      tracePC.show = this.showTraces
      return tracePC
    },

    addAnalyticalTraces(
      traces,
      params = {
        primitiveCollection: this.orbitTracesPC,
        dashed: false,
        colorsArray: undefined,
        colorAlpha: 1,
        show: true
      }
    ) {
      if (!traces || !traces.length) {
        return
      }
      const [colorsArray, dashed, colorAlpha, primitiveCollection, show] =
        this.getCheckedParams(traces, params)

      let colorIndex = -1
      traces.forEach((constellation, constellationIndex) => {
        constellation.forEach((tracePositions, traceIndex) => {
          colorIndex += 1
          const color = colorsArray[colorIndex]
          const positionsArray = [].concat.apply([], tracePositions)
          const polylineMaterial = this.createPolylineMaterial(
            dashed,
            color,
            colorAlpha
          )
          this.addOneAnalyticalTrace(
            primitiveCollection,
            positionsArray,
            polylineMaterial,
            show
          )
        })
      })
    },

    addOneAnalyticalTrace(
      primitiveCollection,
      positionsArray,
      polylineMaterial,
      show = true
    ) {
      primitiveCollection.add({
        positions: Cesium.Cartesian3.unpackArray(positionsArray),
        width: 2,
        material: polylineMaterial,
        show: show === false ? false : true
      })
    },

    addNumericalTraces(
      traces,
      params = {
        primitiveCollection: this.orbitTracesPC,
        dashed: false,
        colorsArray: undefined,
        colorAlpha: 1,
        show: true
      }
    ) {
      if (!traces) {
        return
      }
      let [colorsArray, dashed, colorAlpha, primitiveCollection, show] =
        this.getCheckedParams(traces, params)
      let tracesPC = []
      let nbOfOrbits = 0
      let nbOfPlanes = 0
      const geometries = Object.values(traces)
      geometries.forEach((tracePairs, geometryIndex) => {
        const traces = Object.values(tracePairs)
        primitiveCollection = this.initOneTracesPC()
        nbOfPlanes = 0

        let orbitalPeriod = this.computeOrbitalPeriodFromPosArray(
          Cesium.Cartesian3.unpack(traces[0][0])
        )
        let nbOfOrbitsPerTrace =
          this.currentMission.simulationDuration / orbitalPeriod
        let nbOfPositionsPerOrbit = Math.ceil(
          traces[0].length / nbOfOrbitsPerTrace
        )
        traces.forEach((tracePositions, traceIndex) => {
          nbOfOrbits = 0
          const color = colorsArray[geometryIndex]

          for (let i = 0; i < nbOfOrbitsPerTrace; i++) {
            let slicedArray = tracePositions.slice(
              i * nbOfPositionsPerOrbit,
              (i + 1) * nbOfPositionsPerOrbit /* +
                Math.floor(nbOfPositionsPerOrbit * 0.05) */
            )
            const positionsArray = [].concat.apply([], slicedArray)
            const polylineMaterial = this.createPolylineMaterial(
              dashed,
              color,
              colorAlpha
            )

            primitiveCollection.add({
              positions: Cesium.Cartesian3.unpackArray(positionsArray),
              width: 2,
              material: polylineMaterial,
              show: show === false ? false : true
            })
            nbOfOrbits++
          }
          nbOfPlanes++
        })
        const timeInterval = this.createTimeIntervalCollection(
          nbOfOrbits,
          nbOfPlanes,
          orbitalPeriod
        )
        tracesPC.push({
          primitiveCollection,
          nbOfOrbits,
          nbOfPlanes,
          timeInterval
        })
      })
      return tracesPC
    },

    addNumericalTracesWithoutTimeIntervals(
      traces,
      params = {
        primitiveCollection: this.orbitTracesPC,
        dashed: false,
        colorsArray: undefined,
        colorAlpha: 1,
        show: true
      }
    ) {
      if (!traces) {
        return
      }
      let [colorsArray, dashed, colorAlpha, primitiveCollection, show] =
        this.getCheckedParams(traces, params)
      const geometries = Object.values(traces)
      let previousGeometryIndex = undefined
      let previousPlaneNumber = undefined
      let color = null
      geometries.forEach((tracePairs, geometryIndex) => {
        const tracePairsValues = Object.values(tracePairs)
        const tracesNames = Object.keys(tracePairs)
        tracePairsValues.forEach((tracePositions, traceIndex) => {
          let currentPlaneNumber = parseInt(
            tracesNames[traceIndex].match(/(?<=plane-)(.*)(?=-spacecraft)/)[0]
          )
          if (
            currentPlaneNumber !== previousPlaneNumber ||
            geometryIndex !== previousGeometryIndex
          ) {
            color =
              geometries.length > 1
                ? colorsArray[geometryIndex]
                : colorsArray[currentPlaneNumber - 1]
            const positionsArray = [].concat.apply([], tracePositions)
            this.createOrbitalPlane(positionsArray, color, show)
            previousPlaneNumber = currentPlaneNumber
            previousGeometryIndex = geometryIndex
          }
        })
      })
    },

    /**
     * Create an orbital plane using the given positions and rgb arrays
     * @param posArray - the positions array
     * @param rgbColor - the rgb array
     */
    createOrbitalPlane(posArray, cesiumColor, show = true) {
      const polylineGeometry = new Cesium.PolylineGeometry({
        positions: Cesium.Cartesian3.unpackArray(posArray)
      })

      const polylineInstance = new Cesium.GeometryInstance({
        geometry: polylineGeometry,
        attributes: {
          color: Cesium.ColorGeometryInstanceAttribute.fromColor(cesiumColor),
          show: new Cesium.ShowGeometryInstanceAttribute(show)
        }
      })

      const polylinePrimitive = new Cesium.Primitive({
        geometryInstances: polylineInstance,
        appearance: new Cesium.PolylineColorAppearance()
      })

      this.rotatingOrbitalPlanes.add(polylinePrimitive)
    },

    createTimeIntervalCollection(nbOfOrbits, nbOfPlanes, orbitalPeriod) {
      let orbitsTimeIntervalProperty =
        new Cesium.TimeIntervalCollectionProperty()
      const startTime = this.viewer.clock.startTime
      let intervalStart = new Cesium.JulianDate()
      let intervalStop = new Cesium.JulianDate()
      let planesToShow = []
      for (let i = 0; i < nbOfPlanes; i++) {
        planesToShow.push(i * nbOfOrbits)
      }
      for (let i = 0; i < nbOfOrbits; i++) {
        Cesium.JulianDate.addSeconds(
          startTime,
          Math.floor(i * orbitalPeriod),
          intervalStart
        )
        Cesium.JulianDate.addSeconds(
          intervalStart,
          Math.floor(orbitalPeriod),
          intervalStop
        )
        orbitsTimeIntervalProperty.intervals.addInterval(
          new Cesium.TimeInterval({
            start: intervalStart,
            stop: intervalStop,
            isStartIncluded: true,
            isStopIncluded: true,
            data: planesToShow.map((planeIndex) => planeIndex + i)
          })
        )
      }
      return orbitsTimeIntervalProperty
    },

    getCheckedParams(traces, params) {
      let { colorsArray, dashed, colorAlpha, primitiveCollection, show } =
        params
      if (!colorsArray) {
        let nbOfColors =
          traces.length > 1
            ? traces.reduce((total, current) => total + current.length, 0)
            : traces[0].length
        colorsArray = CesiumTools.createColorsArray(nbOfColors)
      }
      colorAlpha = colorAlpha ? colorAlpha : 1
      CesiumTools.removePrimitivesFromPC(primitiveCollection)
      primitiveCollection = primitiveCollection
        ? primitiveCollection
        : this.orbitTracesPC
      return [colorsArray, dashed, colorAlpha, primitiveCollection, show]
    },

    createPolylineMaterial(dashed, color, colorAlpha) {
      let polylineMaterial = dashed
        ? new Cesium.Material({
            fabric: {
              type: 'PolylineDash',
              uniforms: {
                color: color
              }
            }
          })
        : new Cesium.Material({
            fabric: {
              type: 'Color',
              uniforms: {
                color: Cesium.Color.fromAlpha(color, colorAlpha)
              }
            }
          })
      return polylineMaterial
    },

    computeOrbitalPeriodFromPosArray(firstSatPos) {
      const orbitalPeriod = CesiumConstellationsTools.getOrbitalPeriodFromSMA(
        Cesium.Cartesian3.magnitude(firstSatPos)
      )
      return orbitalPeriod
    },

    updateOrbitPath(val) {
      if (this.orbitalPlanesCollection) this.orbitalPlanesCollection.removeAll()
      this.orbitalPlanesCollection = new Cesium.PrimitiveCollection()
      let polylinePosArray = []
      if (!val) {
        return
      }
      let coordinates = val.traces[0][0]

      coordinates.forEach((threeDArray) => {
        polylinePosArray.push(Cesium.Cartesian3.unpack(threeDArray))
      })

      const polylineGeometry = new Cesium.PolylineGeometry({
        positions: polylinePosArray
      })

      const polylineInstance = new Cesium.GeometryInstance({
        geometry: polylineGeometry,
        attributes: {
          color: Cesium.ColorGeometryInstanceAttribute.fromColor(
            new Cesium.Color(1, 0 / 255, 0 / 255, 1)
          )
        }
      })

      const polylinePrimitive = new Cesium.Primitive({
        geometryInstances: polylineInstance,
        appearance: new Cesium.PolylineColorAppearance()
      })

      this.orbitalPlanesCollection.add(polylinePrimitive)
      this.viewer.scene.primitives.add(this.orbitalPlanesCollection)
      this.orbitalPlanesCollection.show = true
    }
  }
}
